import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const RFC9110 = 'https://www.rfc-editor.org/rfc/rfc9110.html';
const FETCH_SPEC = 'https://fetch.spec.whatwg.org/';

/**
 * Response line-ending tolerance (fingerprinting-techniques §2).
 *
 * A tolerant HTTP response parser accepts a bare-CR-terminated header block
 * where a strict one rejects it. That tolerance differs between engines and
 * HTTP libraries, and a malformed server can use it to bucket clients — this is
 * the passive, defensive direction of that idea.
 *
 * WHY THIS IS OFF BY DEFAULT: exercising it requires the origin to serve a
 * bare-CR-terminated header block, which needs a raw HTTP/1.1 server:
 *
 *   - a Cloudflare Worker (or any Workers-style runtime) cannot emit a bare-CR
 *     header block — response headers pass through the platform's HTTP stack,
 *     which normalises them — and over HTTP/2 or HTTP/3, line endings are not
 *     part of the wire format at all.
 *   - a static host always emits well-formed responses.
 *
 * So there is deliberately NO Worker route for this. Set
 * VITE_CR_PROBE_ENDPOINT to a raw HTTP/1.1 server you control —
 * `scripts/cr-probe-server.mjs` is a reference implementation — and the probe
 * exercises the visitor's parser tolerance against it. Without that endpoint
 * the probe plainly reports "not exercised" and measures nothing.
 *
 * When an endpoint IS configured, the observable is whether the response
 * parsed at all: a strict parser rejects the fetch, a tolerant one round-trips.
 */
export const lineEndingsProbe: Probe = {
  id: 'lineEndings',
  label: 'Response line-ending tolerance',
  layer: 'network',
  tier: 'client',
  category: 'browser',
  description: 'Whether the browser HTTP parser tolerates bare-CR-terminated responses',
  sources: [RFC9110, FETCH_SPEC],
  budgetMs: 2000,

  async run(): Promise<ProbeResult> {
    const findings: Finding[] = [];

    // A CR-tolerant probe endpoint is opt-in. It must be a raw HTTP/1.1 origin
    // (see scripts/cr-probe-server.mjs): a Worker cannot emit a bare-CR header
    // block, and HTTP/2 has no line endings on the wire.
    const probeEndpoint =
      (import.meta.env?.VITE_CR_PROBE_ENDPOINT as string | undefined) ?? null;

    if (!probeEndpoint) {
      // Stay silent rather than emit a constant "not exercised" trio. The
      // static deployment cannot configure this (it needs a raw HTTP/1.1
      // origin), so those signals were pure clutter; the catalog entry explains
      // the limitation.
      return { signals: {} };
    }

    // Exercise it: request the CR-terminated resource and see whether the
    // browser's parser accepts it or errors.
    let parsed = false;
    let error: string | null = null;
    let status: number | null = null;
    const t0 = performance.now();
    try {
      const res = await fetch(probeEndpoint, { cache: 'no-store' });
      status = res.status;
      // If the parser rejected the malformed framing, fetch() rejects; if it
      // tolerated it, we get a normal response.
      await res.text();
      parsed = res.ok;
    } catch (e) {
      error = String((e as Error)?.message ?? e);
    }
    const ms = performance.now() - t0;

    if (error) {
      findings.push({
        id: 'lineendings.cr-response-rejected',
        severity: 'info',
        category: 'browser',
        layer: 'network',
        confidence: 'low',
        title: 'Browser rejected a bare-CR-terminated response',
        detail:
          'A strict response parser refuses malformed framing; a tolerant one accepts it. This tells you the visitor’s parser is strict, which is a weak engine/library signal on its own — many correct parsers are strict, so this is informational and contributes no score.',
        sources: [RFC9110],
        evidence: [`error: ${error}`],
        scoreContribution: 0,
      });
    }

    return {
      signals: {
        available: ok(true, 'derived'),
        exercised: ok(true, 'derived'),
        parsed: ok(parsed, 'client'),
        status: ok(status, 'client'),
        error: ok(error, 'client'),
        roundTripMs: ok(sig(ms, 2), 'client'),
        // A tolerant parser round-trips; a strict one errors. Recorded as a
        // stable engine property.
        parserTolerance: ok(parsed ? 'tolerant' : 'strict', 'derived'),
      },
      findings,
      // NO fingerprint part. Parser tolerance is only measurable when a
      // cooperating bare-CR origin is configured, and that is off by default;
      // contributing a part conditionally would make the hash differ between
      // deployments for a reason that has nothing to do with the device. The
      // stability spec (NO_PART_PROBES) pins this rather than trusting the
      // runtime condition.
    };
  },
};

registerProbe(lineEndingsProbe);
